import { Category } from '@repo/types';
import { Icon, SkeletonCard, Typography } from '@repo/ui';
import c from 'classnames';
import { Badge, Card } from 'components';
import useGetKitIdParam from 'hooks/useGetKitIdParam';
import { useNavigate } from 'react-router-dom';
import useFetchAndGroupCategories from 'services/insights/useFetchAndGroupCategories';
import useCategorySectionEvaluations from 'services/kits/useCategorySectionEvaluations';
import {
  calculateNavigationBadge,
  categoryAnchorId,
  countCategoryEvaluations,
  getSectionEvaluationBadge,
  getTopThreePrioritiesFromCategories,
  PRO_CATEGORY_NAME,
  sortCategoriesByStatusAndPriority,
} from 'views/Results/utils/categoriesAndMetrics';
import styles from './YourCategoryScores.module.css';

function LoadingState() {
  return (
    <div className={styles.cardsContainer}>
      {[...Array(8)].map((_, idx) => (
        <SkeletonCard key={idx} containerClassName={styles.skeleton} />
      ))}
    </div>
  );
}

export default function YourCategoryScoresCards() {
  const { categories, loading } = useFetchAndGroupCategories();
  const currentKitId = useGetKitIdParam();
  const sectionEvaluations = useCategorySectionEvaluations(currentKitId);
  const navigate = useNavigate();

  if (loading) return <LoadingState />;

  const visibleCategories = sortCategoriesByStatusAndPriority(
    categories.filter(c => c.name !== PRO_CATEGORY_NAME),
    sectionEvaluations,
  );

  // Global top 3 by priority across all shown categories, distributed onto their cards.
  const topThreeSubmetrics =
    getTopThreePrioritiesFromCategories(visibleCategories);

  function handleNavigate(category: Category) {
    const parsedCategoryAnchorId = categoryAnchorId(category);
    const categoryUrl = `/results/${currentKitId}/metrics/all-metrics#${parsedCategoryAnchorId}`;

    navigate(categoryUrl);
  }

  return (
    <div className={styles.cardsContainer}>
      {visibleCategories.map(category => {
        const [badgeTitle, badgeState] =
          getSectionEvaluationBadge(
            sectionEvaluations.get(category.name)?.section_evaluation,
          ) ?? calculateNavigationBadge(countCategoryEvaluations(category), false);

        const categorySubmetrics = new Set(
          category.orderedMetrics.flatMap(metric => metric.orderedSubmetrics),
        );
        const prioritizedSubmetrics = topThreeSubmetrics.filter(submetric =>
          categorySubmetrics.has(submetric),
        );

        const hasTopSubmetrics = prioritizedSubmetrics.length > 0;

        return (
          <Card
            key={category.name}
            className={styles.categoryCard}
            cardBodyClassName={styles.categoryCardBody}
            onClick={() => handleNavigate(category)}
          >
            <div
              className={c(
                styles.titleRow,
                hasTopSubmetrics && styles.hasTopSubmetrics,
              )}
            >
              <Typography variant={'body-s'} className={styles.title}>
                {category.name}
              </Typography>
              <div className={styles.badgeAndIcon}>
                <Badge
                  title={badgeTitle}
                  state={badgeState}
                  className={styles.badge}
                />
                <Icon name='chevronForward' className='hide-on-desktop' />
              </div>
            </div>
            {hasTopSubmetrics && (
              <div className={c(styles.focusOn, styles[badgeState])}>
                <Typography variant='body-xs' weight='medium' color='grey'>
                  What to prioritize
                </Typography>
                <Typography variant='body-xs' color='grey'>
                  {prioritizedSubmetrics
                    .map(submetric => submetric.name)
                    .join(', ')}
                </Typography>
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
}
